import { EquipmentStateSelectV2 } from '@frontend/equipment/select';
import { EquipmentState, EquipmentStateName } from '@frontend/equipment/types';
import { CommonMessage } from '@frontend/lang';
import { StockExtended } from '@frontend/stock/types';
import type { ComponentProps } from 'react';
import { FormattedMessage } from 'react-intl';

import KioskList from '../../../../components/kiosk-list.component';
import WizardScreenContainer from '../../../components/wizard-screen-container/wizard-screen-container.component';
import { ProductCard } from '../../browse-products-by-categories/product-card/product-card.component';
import useEquipmentSelectionList from './equipment-selection-list.controller';

const ID = 'equipment-selection-list';
export interface EquipmentSelectionListProps {
    onBack: () => void;
    onSubmit: (_stock: StockExtended[]) => void | Promise<void>;
    isSubmitting?: boolean;
}

const EquipmentSelectionList = (props: EquipmentSelectionListProps) => {
    const viewProps = useEquipmentSelectionList(props);

    return (
        <WizardScreenContainer
            id={ID}
            header={{ onBack: viewProps.onBack }}
            footer={{
                onBack: viewProps.onBack,
                onSubmit: viewProps.onContinue,
                submitDisabled: viewProps.selectedCount === 0 || viewProps.isSubmitting,
                submitLabel: (
                    <FormattedMessage
                        id='manage-spot.lending-equipments.selection.open-selected'
                        defaultMessage='Open selected SLOTs'
                    />
                )
            }}
            loadingModal={{ show: viewProps.isSubmitting }}
            containerClassName='flex size-full flex-col items-start justify-start gap-4 overflow-y-auto px-6 py-4'>
            <div className='bg-background-surface inline-flex flex-col items-start justify-start gap-3 self-stretch rounded-xl p-4'>
                <div className='inline-flex items-start justify-between gap-3 self-stretch'>
                    <div className='inline-flex min-w-0 flex-col items-start justify-start gap-1'>
                        <div className='text-foreground-default text-base font-semibold'>
                            <FormattedMessage
                                id='manage-spot.lending-equipments.selection.title'
                                defaultMessage='Select equipment'
                            />
                        </div>
                        <div className='text-brand-muted text-sm font-medium'>
                            <FormattedMessage
                                id='manage-spot.lending-equipments.selection.message'
                                defaultMessage='Choose equipment and open the linked SLOTs.'
                            />
                        </div>
                    </div>
                    <button
                        type='button'
                        className='text-brand-primary shrink-0 text-base font-medium'
                        onClick={viewProps.toggleAll}>
                        {viewProps.allSelected ? (
                            CommonMessage.BUTTONS.CLEAR
                        ) : (
                            <FormattedMessage
                                id='select-all.message'
                                defaultMessage='Select all'
                            />
                        )}
                    </button>
                </div>
                <EquipmentStateSelectV2
                    id='lending-equipment-state-filter'
                    label={
                        <FormattedMessage
                            id='manage-spot.lending-equipments.filter.label'
                            defaultMessage='Equipment state'
                        />
                    }
                    value={viewProps.activeFilter}
                    subset={stateFilterOptionValues}
                    includeAnyState
                    anyStateLabel='All'
                    anyStateDisplay={
                        <FormattedMessage
                            id='manage-spot.lending-equipments.filter.all'
                            defaultMessage='All'
                        />
                    }
                    onSelect={(option) =>
                        viewProps.setActiveFilter((option?.value as EquipmentStateName | undefined) ?? EquipmentStateName.EQUIPMENT_ANY_STATE)
                    }
                    disableSearch={false}
                />
                <div className='inline-flex items-center justify-between gap-3 self-stretch'>
                    <div className='text-brand-muted text-sm font-medium'>
                        <FormattedMessage
                            id='manage-spot.lending-equipments.selection.selected-count'
                            defaultMessage='{count, plural, =0 {No equipment selected} one {# equipment selected} other {# equipments selected}}'
                            values={{ count: viewProps.selectedCount }}
                        />
                    </div>
                    <div className='text-brand-muted text-sm font-medium'>
                        <FormattedMessage
                            id='manage-spot.lending-equipments.selection.filter-count'
                            defaultMessage='{count, plural, one {# shown} other {# shown}}'
                            values={{ count: viewProps.visibleStockCount }}
                        />
                    </div>
                </div>
            </div>

            <KioskList<StockExtended>
                id={ID + '-list'}
                pageSize={100}
                fetch={viewProps.fetch}
                onDataLoaded={viewProps.onDataLoaded}
                rowElement={(stock) => (
                    <ListItem
                        stock={stock}
                        selected={viewProps.selectedIds.has(stock.id)}
                        equipment={stock.equipment_id ? viewProps.equipmentById.get(stock.equipment_id) : undefined}
                        onToggle={() => viewProps.toggleSelection(stock.id)}
                    />
                )}
                emptyText={
                    <FormattedMessage
                        id='kiosk.manageSpot.lendingEquipments.noEquipments'
                        defaultMessage='No equipment in this state'
                    />
                }
            />
        </WizardScreenContainer>
    );
};

export default EquipmentSelectionList;

const defaultStateFilters = [EquipmentState.EQUIPMENT_MAINTENANCE_REQUIRED, EquipmentState.EQUIPMENT_REVIEW_REQUIRED, EquipmentState.EQUIPMENT_AVAILABLE];
const getEquipmentState = (stateName: EquipmentStateName) => EquipmentState.ALL.find((state) => state.value === stateName);
const otherStateFilters = EquipmentState.ALL.filter(
    (state) => !defaultStateFilters.includes(state) && ![EquipmentStateName.EQUIPMENT_ANY_STATE, EquipmentStateName.EQUIPMENT_CREATED].includes(state.value)
);
const stateFilterOptions = [...defaultStateFilters, ...otherStateFilters];
const stateFilterOptionValues = stateFilterOptions.map((state) => state.value);

const ListItem = ({
    stock,
    selected,
    equipment,
    onToggle
}: {
    stock: StockExtended;
    selected: boolean;
    equipment?: ComponentProps<typeof ProductCard>['equipment'];
    onToggle: () => void;
}) => {
    return (
        <ProductCard
            product_id={stock.product_id}
            equipment={equipment}
            variant='attributes'
            onClick={onToggle}
            styles={{
                container: selected ? 'outline outline-brand-primary outline-2 -outline-offset-2' : ''
            }}
            description={
                <div className='flex flex-wrap items-center gap-1'>
                    <div
                        data-show-icon='false'
                        className={`${stock.equipment_state ? EquipmentState.getType(stock.equipment_state) : 'bg-brand-muted'} flex items-start justify-center gap-1 rounded-full px-2 py-1`}>
                        <div className='text-background-surface justify-start text-center text-sm font-semibold'>
                            {stock.equipment_state
                                ? getEquipmentState(stock.equipment_state)?.displayName
                                : CommonMessage.ERRORS.UNKNOWN_ENTITY(CommonMessage.OBJECTS.COMMON_FIELDS.STATE)}
                        </div>
                    </div>
                    <div
                        data-show-icon='false'
                        data-variant='Muted'
                        className='bg-brand-muted flex items-start justify-center gap-1 rounded-full px-2 py-1'>
                        <div className='text-background-surface justify-start text-center text-sm font-semibold'>
                            {CommonMessage.OBJECTS.SLOT.DEFAULT} {stock.slot?.name}
                        </div>
                    </div>
                </div>
            }
        />
    );
};
